import React from 'react';
import { ThemeProvider } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { BaseLayout } from './src/layouts/Base';
import theme from './src/styles/tokens';
import { PageProvider } from './src/context/pageContext';
import regularFontUrl from './src/fonts/1d3191cd-ae8c-45e2-bb04-11e96d8fa974.woff2';
import boldFontUrl from './src/fonts/e2d37f34-6bca-473c-b64d-93811f4dc046.woff2';

const queryClient = new QueryClient();
const GTM_ID = process.env.GATSBY_GTM_ID || 'GTM-WCK3FXN';
const RECAPTCHA_SITE_KEY =
  process.env.GATSBY_RECAPTCHA_SITE_KEY ||
  '6LewoUghAAAAADxYO2Jc9GxTfwjStawe2WwaTSAY';

export const wrapPageElement = ({ element, props }) => {
  const pageID = props?.data?.page?.remoteId;
  return (
    <QueryClientProvider client={queryClient}>
      <PageProvider
        pageID={pageID}
        pageUrl={props?.data?.page?.url || props?.location?.pathname}
      >
        <BaseLayout {...props}>{element}</BaseLayout>
      </PageProvider>
    </QueryClientProvider>
  );
};

export const wrapRootElement = ({ element }) => (
  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY} useRecaptchaNet>
    <ThemeProvider theme={theme}>{element}</ThemeProvider>
  </GoogleReCaptchaProvider>
);

export const onRenderBody = ({
  pathname,
  setHeadComponents,
  setPreBodyComponents,
  setHtmlAttributes,
}: any) => {
  const headComponents = [
    <link
      key="museo-sans-regular-preload"
      rel="preload"
      href={regularFontUrl}
      as="font"
      type="font/woff2"
      crossOrigin="anonymous"
    />,
    <link
      key="museo-sans-bold-preload"
      rel="preload"
      href={boldFontUrl}
      as="font"
      type="font/woff2"
      crossOrigin="anonymous"
    />,
    <link
      key="mediahawk-preconnect"
      rel="preconnect"
      href="https://www.dynamicnumbers.mediahawk.co.uk"
      crossOrigin="anonymous"
    />,
  ];

  // Trustpilot widget is above the fold on the homepage so needs loading prioritised
  if (pathname === '/') {
    headComponents.push(
      <link
        key="trustpilot-preconnect"
        rel="preconnect"
        href="https://widget.trustpilot.com"
        crossOrigin="anonymous"
      />,
    );
  }

  const preBodyComponents = [
    <noscript key="gtm-noscript">
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
        title="gtm-frame"
      />
    </noscript>,
  ];

  setHeadComponents(headComponents);
  setPreBodyComponents(preBodyComponents);
  setHtmlAttributes({ lang: 'en-GB' });
};
